<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useCloseAccountModal } from '@/composables/useCloseAccountModal'
import { useManageAuthorisationModal } from '@/composables/useManageAuthorisationModal'
import { useSwiftaidAuthorisationStatus } from '@/composables/useSwiftaidAuthorisationStatus'
import DashboardModal from '@/components/dashboard/ui/DashboardModal.vue'

type CloseAccountChoice = 'deactivate' | 'delete'

const router = useRouter()
const { isOpen, closeCloseAccountModal } = useCloseAccountModal()
const { openManageAuthorisationModal } = useManageAuthorisationModal()
const { isAuthorised } = useSwiftaidAuthorisationStatus()

/** Deactivating stops Gift Aid; there's nothing to stop when already deactivated. */
const deactivateDisabled = computed(() => !isAuthorised.value)

const choice = ref<CloseAccountChoice | null>(null)

watch(isOpen, (open) => {
  if (!open) {
    choice.value = null
  }
})

const onContinue = () => {
  if (!choice.value) return
  if (choice.value === 'deactivate' && deactivateDisabled.value) return

  closeCloseAccountModal()

  if (choice.value === 'deactivate') {
    openManageAuthorisationModal()
    return
  }

  router.push({ name: 'DONOR_FLOW_ManageAccountClose' })
}
</script>

<template>
  <DashboardModal
    :is-open="isOpen"
    modal-class="sa-close-account-modal"
    aria-labelledby="close-account-title"
    aria-describedby="close-account-intro"
    @close="closeCloseAccountModal"
  >
      <div
        class="sa-modal-icon sa-close-account-modal__icon sa-manage-auth-modal__icon--warning"
        aria-hidden="true"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path
            d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h2 id="close-account-title">{{ $t('Dashboard.Account.CloseAccount.Modal.Title') }}</h2>

      <p id="close-account-intro" class="sa-close-account-modal__intro">
        {{ $t('Dashboard.Account.CloseAccount.Modal.Intro') }}
      </p>

      <fieldset class="sa-tax-pack-export-fieldset">
        <legend class="sa-tax-pack-export-legend">
          {{ $t('Dashboard.Account.CloseAccount.Modal.ChoiceLegend') }}
        </legend>
        <label
          class="sa-tax-pack-export-option"
          :class="{ 'sa-tax-pack-export-option--disabled': deactivateDisabled }"
        >
          <input
            v-model="choice"
            type="radio"
            value="deactivate"
            name="close-account-choice"
            :disabled="deactivateDisabled"
          />
          <span>
            <span class="sa-tax-pack-export-option__title">
              {{ $t('Dashboard.Account.CloseAccount.Modal.Deactivate.Title') }}
            </span>
            <span class="sa-tax-pack-export-option__sub">
              {{ $t('Dashboard.Account.CloseAccount.Modal.Deactivate.Sub') }}
            </span>
            <span
              v-if="deactivateDisabled"
              class="sa-tax-pack-export-option__note"
            >
              {{ $t('Dashboard.Account.CloseAccount.Modal.Deactivate.AlreadyDeactivated') }}
            </span>
          </span>
        </label>
        <label class="sa-tax-pack-export-option sa-close-account-modal__option--danger">
          <input
            v-model="choice"
            type="radio"
            value="delete"
            name="close-account-choice"
          />
          <span>
            <span class="sa-tax-pack-export-option__title">
              {{ $t('Dashboard.Account.CloseAccount.Modal.Delete.Title') }}
            </span>
            <span class="sa-tax-pack-export-option__sub">
              {{ $t('Dashboard.Account.CloseAccount.Modal.Delete.Sub') }}
            </span>
          </span>
        </label>
      </fieldset>

    <template #footer>
      <div class="sa-modal-actions">
        <button type="button" class="sa-btn-secondary" @click="closeCloseAccountModal">
          {{ $t('Dashboard.Account.CloseAccount.Modal.Cancel') }}
        </button>
        <button
          type="button"
          class="sa-btn-primary sa-modal-actions__primary"
          :disabled="!choice"
          @click="onContinue"
        >
          {{ $t('Dashboard.Account.CloseAccount.Modal.Continue') }}
        </button>
      </div>
    </template>
  </DashboardModal>
</template>
